import HeaderInternal from "../components/Header/HeaderInternal";
import Footer from "../components/Footer/Footer";
import React, { useContext, useEffect, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { AccountContext } from "../store/AccountContext";
import DefaultLayout from "./DefaultLayout";
import CircularProgress from "@mui/material/CircularProgress";

/**
 * PM5 T1 (chrome, batch "layouts") — converted from MUI v4 + `makeStyles` to
 * MUI v5+ (`@mui/material`) `styled`. See docs/mui-conversion-guide.md.
 */

// was: useStyles().background + useStyles().loading, applied together as
// `className={`${classes.background} ${classes.loading}`}` to a bare <div>
// (which has no `sx` prop, hence `styled`).
//
// The two v4 rules both declared paddingTop/paddingBottom, at different values.
// They had equal specificity, so the winner was source order: JSS emits a
// sheet's rules in the order of the `useStyles` object's keys, so `.loading`
// came second and won. The rendered padding was therefore `theme.spacing(8)`
// (64px), never `background`'s `theme.spacing(4)` — the merged rule below
// carries the values that actually rendered, not both sets.
const LoadingPanel = styled("div")(({ theme }) => ({
  flexGrow: 1,
  textAlign: "center",
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

const LatestLayout = ({ children }) => {
  const { loading, loggedIn, userProfile } = useContext(AccountContext);
  const navigate = useNavigate();

  // Navigating is a side effect — render-body calls double-fire under
  // StrictMode's dev double-render; the ref keeps the push once-only (#691,
  // same pattern as LoggedInLayout).
  const shouldRedirectToPreview = !loading && !loggedIn;
  const redirectedToPreview = useRef(false);
  useEffect(() => {
    if (shouldRedirectToPreview && !redirectedToPreview.current) {
      redirectedToPreview.current = true;
      navigate("/latest-preview");
    }
  }, [shouldRedirectToPreview, navigate]);

  if (shouldRedirectToPreview) {
    return null;
  }

  // Loading Screen
  if (loading && !loggedIn) {
    return (
      <DefaultLayout>
        <LoadingPanel>
          <CircularProgress color="primary" />
        </LoadingPanel>
      </DefaultLayout>
    );
  }

  // Trial Member - Trial Expired - Redirect To Subscription Window
  if (loggedIn && userProfile.memberStatus === "trial-expired") {
    return <Navigate to="/myaccount/overview" replace />;
  }

  // Member is Locked
  if (
    loggedIn &&
    (userProfile.isLocked || userProfile.memberStatus === "new-trial-eligible")
  ) {
    return <Navigate to="/myaccount/overview" replace />;
  }

  // Member is Locked
  if (
    loggedIn &&
    (userProfile.isLocked || userProfile.memberStatus === "locked")
  ) {
    return <Navigate to="/myaccount/overview" replace />;
  }

  // Member is Standard Incomplete - send them to lock them for the time being
  if (
    loggedIn &&
    !loading &&
    userProfile.memberStatus === "standard-incomplete"
  ) {
    return <Navigate to="/myaccount/overview" replace />;
  }

  // Member is Trial
  if (loggedIn && userProfile.memberStatus === "trial") {
    return (
      <>
        <HeaderInternal />
        {children}
        <Footer />
      </>
    );
  }

  // Member has a Expired Subscription (paid at least once)
  if (loggedIn && userProfile.memberStatus === "standard-expired") {
    return <Navigate to="/myaccount/overview" replace />;
  }

  return (
    <>
      <HeaderInternal />
      {children}

      <Footer />
    </>
  );
};

export default LatestLayout;
